import React, { memo, type ReactElement, useCallback } from 'react';
import {
  ApplicationIcon,
  Avatar,
  Box,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_BLD, TABS_FILTERS } from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import { getAbbreviatedNumber, getLogoEndpointUrl } from '../../utils';
import SodApplicationGroup from '../SodApplicationGroup';

import { WIDGET_KINDS } from './SodWidget';

import './SodWidgetListItem.css';

interface SodWidgetListItemProps {
  kind: string;
  tab: string | null;
  iconString?: string;
  text: string;
  count: number;
  entitlementEndpointLabel?: string;
  onClick: () => void;
}

/**
 * SodWidgetListItem
 */
function SodWidgetListItem({
  kind,
  tab,
  iconString,
  text,
  count,
  entitlementEndpointLabel,
  onClick,
}: SodWidgetListItemProps): ReactElement {
  const l = useInvertedLocalize();

  const getListItemIcon = useCallback(() => {
    if (!iconString) {
      if (kind === WIDGET_KINDS.ENDPOINT) {
        return <ApplicationIcon size='medium' />;
      }

      return null;
    }

    if (kind === WIDGET_KINDS.USER) {
      const userName = iconString.split(' ').filter(Boolean);
      const userInitials =
        userName.length >= 2
          ? userName[0].charAt(0).toUpperCase() +
            userName[userName.length - 1].charAt(0).toUpperCase()
          : 'NA';

      return <Avatar size='medium'>{userInitials}</Avatar>;
    }

    if (kind === WIDGET_KINDS.ENDPOINT) {
      const urlString = getLogoEndpointUrl(iconString);

      return <ApplicationIcon size='medium' src={urlString} />;
    }

    if (kind === WIDGET_KINDS.ENTITLEMENT) {
      return <ApplicationIcon icon='Entitlement' size='medium' />;
    }

    return <ApplicationIcon icon={iconString} size='medium' />;
  }, [kind, iconString]);

  const getListItemText = useCallback(() => {
    if (kind === WIDGET_KINDS.ENTITLEMENT) {
      return (
        <>
          <Typography kind='body1' color={CF_NEU_BLD} lineLimit={1}>
            {text}
          </Typography>
          <SodApplicationGroup
            label={entitlementEndpointLabel ?? 'Application'}
            firstLogo={iconString}
          />
        </>
      );
    }

    return (
      <Typography kind='body1' color={CF_NEU_BLD} lineLimit={2}>
        {text}
      </Typography>
    );
  }, [kind, text, iconString, entitlementEndpointLabel]);

  const getListItemCount = useCallback(() => {
    if (tab !== null) {
      const activeTabObject = Object.values(TABS_FILTERS).find(
        (item) => item.value === tab
      );
      const label = `${count} ${
        activeTabObject ? l(activeTabObject.label) : ''
      } ${l('Violations')}`;

      return (
        <Typography
          kind='h1'
          color={CF_NEU_BLD}
          tooltipConfigs={{
            text: label,
            placement: 'top',
          }}
          showTooltipOnHover>
          {getAbbreviatedNumber(count)}
        </Typography>
      );
    }

    return null;
  }, [l, tab, count]);

  return (
    <button
      type='button'
      onClick={onClick}
      className='Sod-WidgetListItem-wrapper'>
      <Box tag='div' className='Sod-WidgetListItem-iconBox'>
        {getListItemIcon()}
      </Box>
      <Box tag='div' className='Sod-WidgetListItem-textBox'>
        {getListItemText()}
      </Box>
      <Box tag='div' className='Sod-WidgetListItem-countBox'>
        {getListItemCount()}
      </Box>
    </button>
  );
}

SodWidgetListItem.defaultProps = {
  iconString: null,
  entitlementEndpointLabel: null,
};

export default memo(SodWidgetListItem);
